/**
 * Parse/serialize array-shaped Custom events field values stored as strings in UI state.
 */

export interface BonusEligibilityRow {
  id: string
  ecommerceSku: string
  provider: string
  ecommerceSkuKey: string
  marketplaceCouponCode: string
  marketplaceCouponCodeExpiration: string
  entitledTo: string
  contentSetId: string
  startDate: string
  endDate: string
}

export const BONUS_ELIGIBILITY_ROW_FIELD_KEYS: ReadonlyArray<keyof BonusEligibilityRow> = [
  'id',
  'ecommerceSku',
  'provider',
  'ecommerceSkuKey',
  'marketplaceCouponCode',
  'marketplaceCouponCodeExpiration',
  'entitledTo',
  'contentSetId',
  'startDate',
  'endDate',
]

const BONUS_ELIGIBILITY_REQUIRED_KEYS: ReadonlyArray<keyof BonusEligibilityRow> = [
  'ecommerceSku',
  'provider',
  'ecommerceSkuKey',
  'startDate',
]

export function createEmptyBonusEligibilityRow (): BonusEligibilityRow {
  return {
    id: '',
    ecommerceSku: '',
    provider: '',
    ecommerceSkuKey: '',
    marketplaceCouponCode: '',
    marketplaceCouponCodeExpiration: '',
    entitledTo: '',
    contentSetId: '',
    startDate: '',
    endDate: '',
  }
}

export function parseStringArrayField (value: string): string[] {
  const trimmed = String(value || '').trim()
  if (!trimmed) {
    return []
  }
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed) as unknown
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean)
      }
    } catch {
      return []
    }
  }
  return trimmed
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
}

export function serializeStringArrayField (items: string[]): string {
  const unique = items.map((item) => item.trim()).filter(Boolean)
  return JSON.stringify(unique)
}

function normalizeBonusRow (raw: Record<string, unknown>): BonusEligibilityRow {
  const row = createEmptyBonusEligibilityRow()
  BONUS_ELIGIBILITY_ROW_FIELD_KEYS.forEach((key) => {
    const v = raw[key]
    if (v !== undefined && v !== null && String(v).length > 0) {
      row[key] = String(v)
    }
  })
  return row
}

export function parseBonusEligibilityRows (value: string): BonusEligibilityRow[] {
  const trimmed = String(value || '').trim()
  if (!trimmed) {
    return []
  }
  try {
    const parsed = JSON.parse(trimmed) as unknown
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed
      .filter((item) => item && typeof item === 'object')
      .map((item) => normalizeBonusRow(item as Record<string, unknown>))
  } catch {
    return []
  }
}

export function serializeBonusEligibilityRows (rows: BonusEligibilityRow[]): string {
  const payload = rows.map((row) => {
    const out: Record<string, unknown> = {}
    BONUS_ELIGIBILITY_ROW_FIELD_KEYS.forEach((key) => {
      const raw = row[key]
      if (raw === undefined || raw === null || String(raw).trim() === '') {
        return
      }
      if (key === 'id' || key === 'contentSetId') {
        const num = Number(raw)
        out[key] = Number.isFinite(num) ? num : raw
        return
      }
      out[key] = raw
    })
    return out
  })
  return JSON.stringify(payload)
}

export function validateBonusEligibilityRows (rows: BonusEligibilityRow[]): string[] {
  const errors: string[] = []
  if (rows.length === 0) {
    errors.push('At least one bonus eligibility row is required')
    return errors
  }
  rows.forEach((row, index) => {
    BONUS_ELIGIBILITY_REQUIRED_KEYS.forEach((key) => {
      if (!String(row[key] || '').trim()) {
        errors.push(`Eligibility row ${index + 1}: ${key} is required`)
      }
    })
  })
  return errors
}

export function buildEcommerceSkuKey (provider: string, ecommerceSku: string): string {
  return `${provider}-${ecommerceSku}`
}
